import axios from "axios";

import { i18n } from "@/localization";
import type { TranslationKey } from "@/localization/resources/keys.ts";

const LOCALE_KEYS_MAP: { [key: string]: TranslationKey } = {
	"jomo.registration.PASSWORD_POLICY_NOT_MET": "validation.passwordInvalid",
	"jomo.registration.EMAIL_INVALID": "validation.emailInvalid",
	"jomo.registration.EMAIL_DOMAIN_NOT_ALLOWED": "validation.emailDomainNotAllowed",
	"jomo.registration.EMAIL_ALREADY_EXISTS": "validation.emailExists",
	"jomo.registration.STEP1_TOKEN_INVALID": "validation.verificationCodeInvalid",
	"jomo.registration.STEP1_TOKEN_EXPIRED": "validation.sessionExpired",
	"jomo.registration.VERIFICATION_CODE_INVALID": "validation.verificationCodeInvalid",
	"jomo.registration.CODE_VERIFICATION_LOCKED": "validation.codeVerificationLocked",
	"jomo.registration.PASSWORD_EMPTY": "validation.passwordEmpty",
	"jomo.registration.PASSWORD_TOO_SHORT": "validation.passwordTooShort",
	"jomo.registration.PASSWORD_TOO_LONG": "validation.passwordTooLong",
	"jomo.registration.PASSWORD_UPPERCASE_REQUIRED": "validation.passwordUppercaseRequired",
	"jomo.registration.PASSWORD_LOWERCASE_REQUIRED": "validation.passwordLowercaseRequired",
	"jomo.registration.PASSWORD_DIGITS_REQUIRED": "validation.passwordDigitsRequired",
	"jomo.registration.PASSWORD_SPECIAL_CHARS_REQUIRED": "validation.passwordSpecialCharsRequired",
	"jomo.registration.PASSWORD_MATCHES_EMAIL": "validation.passwordMatchesEmail",
	"jomo.registration.PASSWORD_CONTAINS_EMAIL": "validation.passwordContainsEmail",
	"jomo.registration.VALIDATION_FAILED": "validation.validationFailed"
};

export interface BackendError {
	messageKey: string;
	params?: Record<string, string>;
}

const BACKEND_ERROR_PREFIX = "jomo.registration.";

export const getBackendErrorDetails = (error: unknown): BackendError[] => {
	if (!axios.isAxiosError(error)) {
		return [];
	}

	const backendErrors: BackendError[] | undefined = error.response?.data?.errors;

	return Array.isArray(backendErrors) ? backendErrors : [];
};

export const findBackendErrorDetail = (error: unknown, code: string): BackendError | undefined =>
	getBackendErrorDetails(error).find((e) => e.messageKey === `${BACKEND_ERROR_PREFIX}${code}`);

export const handleErrorMessage = (error: unknown): string => {
	const backendErrors = getBackendErrorDetails(error);

	if (backendErrors.length > 0) {
		return backendErrors
			.map((e) => {
				const translationKey = LOCALE_KEYS_MAP[e.messageKey];

				return translationKey ? i18n(translationKey, e.params) : i18n("validation.generalError");
			})
			.join(". ");
	}

	return i18n("validation.generalError");
};

export const handleErrorCode = (error: unknown): number => {
	if (!axios.isAxiosError(error)) {
		throw new Error("Unknown error");
	}

	if (typeof error.response?.data?.status === "number") {
		return Number(error.response.data.status);
	}

	return Number(error.response?.status ?? error.status);
};

export const isClientError = (error: unknown): boolean => {
	if (!axios.isAxiosError(error)) {
		return false;
	}

	const errorCode = handleErrorCode(error);

	return errorCode >= 400 && errorCode <= 499;
};
